import * as React from 'react';
import { useRef } from 'react';
import { useCheckbox } from '@react-aria/checkbox';
import { useToggleState } from '@react-stately/toggle';
import { useFocusRing } from '@react-aria/focus';
import { mergeProps } from '@react-aria/utils';
import type { AriaCheckboxProps } from '@react-types/checkbox';
import classnames from 'classnames';
import styles from './AtollonCheckbox.module.css';

export enum CheckboxVariant {
  EMPHASIZED = 'emphasized',
  NEUTRAL = 'neutral'
}

export interface IAtollonCheckboxProps extends Omit<AriaCheckboxProps, 'children'> {
  children?: React.ReactNode;
  className?: string;
  variant?: CheckboxVariant;
  label?: string;
  isRequired?: boolean;
  width?: string;
  labelPosition?: 'top' | 'left';
}

type CheckboxState = 'unchecked' | 'indeterminate' | 'checked';

export const AtollonCheckbox = React.forwardRef<HTMLInputElement, IAtollonCheckboxProps>(
  (props, forwardedRef) => {
    const { 
      children, 
      className, 
      variant = 'emphasized',
      label,
      isRequired,
      width,
      labelPosition = 'left',
      isIndeterminate: isIndeterminateProp,
      onChange,
      ...otherProps 
    } = props;

    // Determine if 3-state mode is enabled (only when isIndeterminate prop is explicitly true)
    const isThreeState = isIndeterminateProp === true;

    // Internal state for 3-state checkbox
    const [internalState, setInternalState] = React.useState<CheckboxState>(() => {
      if (isThreeState) {
        // 3-state mode: determine initial state from props
        if (isIndeterminateProp) return 'indeterminate';
        if (otherProps.isSelected) return 'checked';
        return 'unchecked';
      } else {
        // 2-state mode: only checked or unchecked
        return otherProps.isSelected ? 'checked' : 'unchecked';
      }
    });

    // Sync with external isSelected prop when not in 3-state mode
    React.useEffect(() => {
      if (!isThreeState) {
        setInternalState(otherProps.isSelected ? 'checked' : 'unchecked');
      }
    }, [otherProps.isSelected, isThreeState]);

    const state = useToggleState({
      ...otherProps,
      isSelected: internalState === 'checked',
    });

    const ref = useRef<HTMLInputElement>(null);
    
    // Ensure aria-label is provided when there are no children
    // React Aria requires either children or aria-label for accessibility
    // If label is provided but no aria-label and no children, use label as aria-label
    const checkboxProps = {
      ...otherProps,
      isSelected: internalState === 'checked',
      isIndeterminate: internalState === 'indeterminate',
      // Use label as aria-label if no children and no explicit aria-label provided
      ...(!children && !otherProps['aria-label'] && label ? { 'aria-label': label } : {}),
    };
    
    const { inputProps, isDisabled } = useCheckbox(
      checkboxProps,
      state,
      ref
    );
    const { isFocusVisible, focusProps } = useFocusRing();

    // Handle 3-state cycling
    const handleChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
      if (isDisabled) return;

      if (isThreeState) {
        // Cycle through: unchecked → indeterminate → checked → unchecked
        setInternalState((prev) => {
          let next: CheckboxState;
          if (prev === 'unchecked') {
            next = 'indeterminate';
          } else if (prev === 'indeterminate') {
            next = 'checked';
          } else {
            next = 'unchecked';
          }
          
          // Call onChange with the new state
          if (onChange) {
            onChange(next === 'checked');
          }
          
          return next;
        });
      } else {
        // Standard 2-state behavior: toggle between checked and unchecked
        const newChecked = e.target.checked;
        setInternalState(newChecked ? 'checked' : 'unchecked');
        
        if (onChange) {
          onChange(newChecked);
        }
      }
    }, [isThreeState, isDisabled, onChange]);

    // Merge forwarded ref with internal ref
    React.useImperativeHandle(forwardedRef, () => ref.current as HTMLInputElement);

    const displayLabel = label || children;
    const labelText = displayLabel ? `${displayLabel}${isRequired ? ' *' : ''}` : null;

    const checkboxWrapperClass = classnames(styles.checkboxWrapper, className, {
      [styles.labelTop]: labelPosition === 'top',
      [styles.labelLeft]: labelPosition === 'left',
    });

    const isIndeterminate = internalState === 'indeterminate';
    const isChecked = internalState === 'checked';

    return (
      <div className={checkboxWrapperClass} style={{ width }}>
        <label
          className={classnames(styles.checkbox, {
            [styles.isSelected]: isChecked,
            [styles.isDisabled]: isDisabled,
            [styles.isIndeterminate]: isIndeterminate,
            [styles.isFocusVisible]: isFocusVisible,
            [styles.emphasized]: variant === 'emphasized',
            [styles.neutral]: variant === 'neutral',
          })}
        >
          <input
            {...mergeProps(inputProps, focusProps)}
            ref={ref}
            type="checkbox"
            className={styles.input}
            checked={isChecked}
            onChange={handleChange}
            aria-checked={isIndeterminate ? 'mixed' : isChecked}
          />
          <span className={styles.checkmark} aria-hidden="true">
            {isChecked && (
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M13.5 4L6 11.5 2.5 8"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  fill="none"
                />
              </svg>
            )}
            {isIndeterminate && (
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M2 8H14"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </span>
          {labelText && <span className={styles.label}>{labelText}</span>}
        </label>
      </div>
    );
  }
);

AtollonCheckbox.displayName = 'AtollonCheckbox';

